import {
  Avatar,
  Button,
  Tooltip,
  TableRow,
  MenuItem,
  TableCell,
  Typography,
  IconButton,
} from '@mui/material';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import CenterStack from 'src/components/ui-kit/center-stack';
import { useBoolean } from 'src/hooks/use-boolean';
import { BankAccountType } from 'src/type/bank';

type Props = {
  row: BankAccountType;
  loading: boolean;
  onEdit: VoidFunction;
  onSetDefault: VoidFunction;
  onToggleStatus: VoidFunction;
  handleDelete: VoidFunction;
};

export default function SettingBankAccountRow({
  row,
  onEdit,
  loading,
  onSetDefault,
  onToggleStatus,
  handleDelete,
}: Props) {
  const popover = usePopover();
  const { bank, accountNumber, accountName, isEnable, isDefault } = row;
  const confirm = useBoolean();

  return (
    <>
      <TableRow>
        <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar alt="" src={bank?.imageUrl} sx={{ width: 24, height: 24, mr: 1 }}>
            {bank?.name?.toUpperCase()}
          </Avatar>
          <Typography variant="body2" noWrap>
            {bank?.name}
          </Typography>
          {isDefault && (
            <CenterStack sx={{ ml: 1, cursor: 'pointer' }}>
              <Tooltip title="บัญชีหลัก">
                <Iconify icon="solar:flag-2-bold-duotone" color="primary.main" />
              </Tooltip>
            </CenterStack>
          )}
        </TableCell>

        <TableCell>
          <Typography variant="body2" noWrap>
            {accountNumber}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" noWrap>
            {accountName}
          </Typography>
        </TableCell>
        <TableCell>
          <Label
            variant="soft"
            color={(isEnable && 'success') || (!isEnable && 'error') || 'default'}
          >
            {isEnable ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
          </Label>
        </TableCell>

        <TableCell>
          <IconButton
            color={popover.open ? 'inherit' : 'default'}
            onClick={popover.onOpen}
            disabled={loading}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="top-right"
        sx={{ width: 160 }}
      >
        {!isDefault && (
          <MenuItem
            onClick={() => {
              popover.onClose();
              onSetDefault();
            }}
          >
            <Iconify icon="solar:flag-2-bold-duotone" />
            ตั้งเป็นบัญชีหลัก
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            popover.onClose();
            onToggleStatus();
          }}
          sx={{ color: isEnable ? 'error.main' : 'success.main' }}
        >
          <Iconify icon="ion:switch" />
          {isEnable ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
        </MenuItem>

        <MenuItem
          onClick={() => {
            popover.onClose();
            onEdit();
          }}
        >
          <Iconify icon="solar:pen-bold-duotone" />
          แก้ไข
        </MenuItem>

        <MenuItem
          onClick={() => {
            popover.onClose();
            confirm.onTrue();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          ลบบัญชี
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="ลบบัญชีธนาคาร"
        content={`คุณต้องการลบบัญชีธนาคาร ${bank?.name} ${accountNumber} ใช่หรือไม่?`}
        action={
          <Button
            variant="contained"
            color="inherit"
            onClick={() => {
              handleDelete();
              confirm.onFalse();
            }}
          >
            ยืนยันการลบ
          </Button>
        }
      />
    </>
  );
}
